import { Injectable, OnDestroy } from '@angular/core';
import { Subject, combineLatest, distinctUntilChanged, filter, takeUntil } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AlertDto } from '../models/alerts.models';
import { ProjectContextService } from './project-context.service';
import { AuthService } from './auth.service';

export type ThermalBreakStatus = 'cold' | 'overCold' | 'hot' | 'overHot' | 'out';

export type ThermalBreakPayload = {
  status: ThermalBreakStatus;
  before?: ThermalBreakStatus;
  zone_id: string;
  time_in: number;
  bounce_counts?: number;
  no_connection?: boolean;
};

export type ThermalBreakEvent = {
  type: 'tb';
  time_start: number;
  time_end?: number;
  asset_id: string;
  project_id: string;
  payload: ThermalBreakPayload;
  asset_name?: string | null;
  asset_batery?: number | null;
  zone_name?: string | null;
};

export type AlertResolvedEvent = {
  _id: string;
  project_id: string;
};

@Injectable({ providedIn: 'root' })
export class SseEventsService implements OnDestroy {
  private source: EventSource | null = null;
  private readonly destroy$ = new Subject<void>();

  private readonly tbSubject = new Subject<ThermalBreakEvent>();
  private readonly alertSubject = new Subject<AlertDto>();
  private readonly alertResolvedSubject = new Subject<AlertResolvedEvent>();

  readonly tb$ = this.tbSubject.asObservable();
  readonly alert$ = this.alertSubject.asObservable();
  readonly alertResolved$ = this.alertResolvedSubject.asObservable();

  constructor(
    private readonly projectContext: ProjectContextService,
    private readonly auth: AuthService,
  ) {
    combineLatest([this.projectContext.projectId$, this.auth.token$])
      .pipe(
        takeUntil(this.destroy$),
        filter(([projectId, token]) => !!projectId && !!token),
        distinctUntilChanged(
          ([prevProject, prevToken], [nextProject, nextToken]) =>
            prevProject === nextProject && prevToken === nextToken,
        ),
      )
      .subscribe(([projectId, token]) => {
        if (!projectId || !token) return;
        this.connect(projectId, token);
      });

    this.projectContext.projectId$
      .pipe(
        takeUntil(this.destroy$),
        filter((projectId) => !projectId),
      )
      .subscribe(() => this.disconnect());

    this.auth.token$
      .pipe(
        takeUntil(this.destroy$),
        filter((token) => !token),
      )
      .subscribe(() => this.disconnect());
  }

  ngOnDestroy(): void {
    this.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private connect(projectId: string, token: string) {
    this.disconnect();
    const url = `${environment.apiBaseUrl}/sse/channels/${encodeURIComponent(projectId)}/stream?token=${encodeURIComponent(token)}`;
    this.source = new EventSource(url);

    this.source.addEventListener('tb', (evt) => {
      try {
        const data = JSON.parse((evt as MessageEvent).data) as ThermalBreakEvent;
        this.tbSubject.next(data);
      } catch {
        // ignore malformed events
      }
    });

    this.source.addEventListener('alert', (evt) => {
      try {
        const data = JSON.parse((evt as MessageEvent).data) as AlertDto;
        this.alertSubject.next(data);
      } catch {
        // ignore malformed events
      }
    });

    this.source.addEventListener('alert_resolved', (evt) => {
      try {
        const data = JSON.parse((evt as MessageEvent).data) as AlertResolvedEvent;
        this.alertResolvedSubject.next(data);
      } catch {
        // ignore malformed events
      }
    });
  }

  private disconnect() {
    if (this.source) {
      this.source.close();
      this.source = null;
    }
  }
}
